import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { trackEvent } from '../utils/analytics';

/**
 * Two-step verify so email-link prefetchers (Gmail, Outlook safe-links,
 * antivirus URL scanners) don't accidentally consume the one-time token.
 * The token is only POSTed to /api/auth/email/verify when the user clicks
 * the button on this page.
 */
const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmailToken } = useAuth();

  const token = searchParams.get('token') || '';
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Closes the other end of the magic-link funnel: email_link_requested fires when
  // the link is sent, this fires when it is opened. The gap between them is the
  // drop-off we previously could not see at all. `hasToken:false` means someone
  // reached this page without a token in the URL (truncated/mangled link).
  useEffect(() => {
    trackEvent('email_verify_opened', { hasToken: !!token });
  }, [token]);

  const handleSignIn = async () => {
    if (!token) return;
    setSigning(true);
    setError(null);
    try {
      await verifyEmailToken(token);
      navigate('/profile', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(message);
      setSigning(false);
      // Expired or already-consumed links land here. Previously this dead-ended in
      // component state with no signal anywhere.
      trackEvent('email_verify_failed', { reason: message.slice(0, 80) });
    }
  };

  return (
    <div className="min-h-screen bg-crypto-dark flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="bg-crypto-gray border border-gray-700 rounded-xl p-8 max-w-md w-full text-center">
          {!token ? (
            <>
              <div className="text-5xl mb-4">⚠️</div>
              <h1 className="text-xl font-semibold text-white mb-2">No token found</h1>
              <p className="text-gray-400 text-sm mb-6">
                This page expected a sign-in token in the URL.
              </p>
              <Link
                to="/"
                className="inline-block bg-gray-700 hover:bg-gray-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
              >
                Back to home
              </Link>
            </>
          ) : (
            <>
              <div className="text-5xl mb-4">🔐</div>
              <h1 className="text-xl font-semibold text-white mb-2">Sign in to deepnets.ai</h1>
              <p className="text-gray-400 text-sm mb-6">
                Click below to complete sign-in. This link can only be used once.
              </p>
              <button
                onClick={handleSignIn}
                disabled={signing}
                className="w-full bg-crypto-accent hover:bg-crypto-accent/80 disabled:opacity-50 text-crypto-dark font-medium py-2.5 px-5 rounded-lg transition-colors"
              >
                {signing ? 'Signing in…' : 'Sign In →'}
              </button>
              {error && (
                <p className="mt-4 text-red-400 text-sm">{error}</p>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VerifyEmailPage;
